
import { useState, useEffect } from "react";
import { Form, Lead, UserProfile } from "@/types";
import { supabase } from "@/integrations/supabase/client";
import { parseFormData, parseLeadData } from "@/utils/supabase-utils";
import { getDefaultFormTemplate } from "@/utils/form-utils";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

export const useDashboardData = (userId: string) => {
  const { data: userProfile, isLoading: isProfileLoading } = useQuery({
    queryKey: ['userProfile', userId],
    queryFn: async () => {
      if (!userId) return null;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('name, interested_investors_count')
          .eq('id', userId)
          .single();
          
        if (error) {
          console.error("Error fetching user profile:", error);
          return null;
        }
        
        return data as UserProfile;
      } catch (err) {
        console.error("Unexpected error fetching profile:", err);
        return null;
      }
    },
    enabled: !!userId,
    staleTime: 1000 * 60, // Consider data stale after 1 minute
    refetchOnWindowFocus: true, // Refetch when the window regains focus
  });
  
  const { data: forms = [], isLoading: isFormsLoading } = useQuery({
    queryKey: ['forms', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      try {
        const { data: formsData, error: formsError } = await supabase
          .from('forms')
          .select('*')
          .eq('user_id', userId)
          .order('updated_at', { ascending: false });
          
        if (formsError) {
          console.error("Error loading forms:", formsError);
          return [];
        }
        
        console.log("Forms data loaded:", formsData);
        const defaultTemplate = getDefaultFormTemplate();
        return (formsData || []).map(form => 
          parseFormData(form, defaultTemplate)
        );
      } catch (err) {
        console.error("Unexpected error fetching forms:", err);
        return [];
      }
    },
    enabled: !!userId,
  });
  
  const { data: recentLeads = [], isLoading: isLeadsLoading } = useQuery({
    queryKey: ['recentLeads', userId],
    queryFn: async () => {
      if (!userId) return [];
      
      try {
        const { data: leadsData, error: leadsError } = await supabase
          .from('leads')
          .select('*, forms!inner(*)')
          .eq('forms.user_id', userId)
          .order('created_at', { ascending: false })
          .limit(5);
          
        if (leadsError) {
          console.error("Error loading leads:", leadsError);
          return [];
        }
        
        const typedLeads: Lead[] = (leadsData || []).map(lead => {
          const parsedData = parseLeadData(lead.data);
          
          const formFields = lead.forms?.fields || [];
          
          const labeledData: Record<string, string> = {};
          
          Object.entries(parsedData).forEach(([fieldId, value]) => {
            if (Array.isArray(formFields)) {
              const matchingField = formFields.find((field: any) => field.id === fieldId);
              if (matchingField && typeof matchingField === 'object' && matchingField !== null && 'label' in matchingField) {
                labeledData[matchingField.label as string] = value as string;
              } else {
                labeledData[fieldId] = value as string;
              }
            } else {
              labeledData[fieldId] = value as string;
            }
          });
          
          if (lead.location_data && typeof lead.location_data === 'object' && lead.location_data !== null) {
            const locationData = lead.location_data as Record<string, any>;
            const locationParts = [];
            
            if (locationData.city && typeof locationData.city === 'string') {
              locationParts.push(locationData.city);
            }
            
            if (locationData.region && typeof locationData.region === 'string') {
              locationParts.push(locationData.region);
            }
            
            if (locationData.country_name && typeof locationData.country_name === 'string') {
              locationParts.push(locationData.country_name);
            }
            
            if (locationParts.length > 0) {
              labeledData["Location"] = locationParts.join(", ");
            }
          }
          
          let newStatus: 'cold' | 'warm' | 'hot' = 'cold';
          
          if (lead.status === 'new' || lead.status === 'contacted') {
            newStatus = 'cold';
          } else if (lead.status === 'qualified') {
            newStatus = 'warm';
          } else if (lead.status === 'disqualified') {
            newStatus = 'hot';
          } else if (lead.status === 'cold' || lead.status === 'warm' || lead.status === 'hot') {
            newStatus = lead.status as 'cold' | 'warm' | 'hot';
          }
          
          return {
            id: lead.id,
            formId: lead.form_id,
            data: labeledData,
            createdAt: new Date(lead.created_at || new Date()),
            status: newStatus
          };
        });
        
        // Update interested investors count if needed
        if (leadsData && leadsData.length > 0) {
          supabase
            .from('profiles')
            .update({ interested_investors_count: leadsData.length })
            .eq('id', userId)
            .then(({ error }) => {
              if (error) {
                console.error("Error updating interested investors count:", error);
              }
            });
        }
        
        return typedLeads;
      } catch (err) {
        console.error("Unexpected error fetching leads:", err);
        return [];
      }
    },
    enabled: !!userId,
  });

  const isLoading = isProfileLoading || isFormsLoading || isLeadsLoading;

  return { forms, recentLeads, isLoading, userProfile };
};
